import { environmentCode } from "~/lib/environment";

/**
 * Every Road.io integration the app offers. Mirrors ROAD_PROVIDERS in
 * api/src/lib/road.ts, which lists everything else a new Road partner needs.
 *
 * Road (the platform behind E-Flux) is multi-tenant: each brand on it signs in
 * through its own OAuth provider, which we always name `<brand>-road`. Every one
 * is an OAuth CPO offered for every hardware brand, like Plugchoice, so it needs
 * no charger-catalog entry: the connect chooser, the onboarding journey and the
 * label maps all derive their Road entries from this list.
 *
 * Logos: every provider ships its own files in `public/` (`{id}-svg.svg`,
 * `{id}-icon.svg`, `{id}-icon-mono.svg`); there is no shared Road fallback.
 */
export type RoadProvider = {
  /** Better Auth provider id, connect-option id and `via`. */
  id: `${string}-road`;
  /** Customer-facing name; the raw id must never reach the screen. */
  label: string;
  /**
   * The environments (`VITE_ENVIRONMENT_CODE`) whose connect chooser offers it,
   * or null for every environment. Narrowed while the integration has not run
   * against a real account yet; elsewhere it is withheld (see
   * `isDevOnlyBackoffice`).
   */
  offeredIn: readonly string[] | null;
};

export const ROAD_PROVIDERS: readonly RoadProvider[] = [
  { id: "kia-road", label: "Kia Charge", offeredIn: null },
  // The successor of the credential-based `eflux` backoffice, which the
  // chooser no longer offers (see CPO_OPTIONS).
  { id: "eflux-road", label: "E-Flux by Road", offeredIn: null },
];

/** Whether the connect chooser offers this provider on the current environment. */
export const isRoadProviderOffered = (provider: RoadProvider): boolean =>
  provider.offeredIn === null || provider.offeredIn.includes(environmentCode());

/** Customer-facing name per Road providerId. */
export const ROAD_PROVIDER_LABELS: Record<string, string> = Object.fromEntries(
  ROAD_PROVIDERS.map(({ id, label }) => [id, label]),
);
